import FraudsApi from '@/api/frauds.api'

const CACHE_TTL_MS = 10 * 60 * 1000 // 10 minutes

const SLACK_TEAM_ID = 'T7YDQLFBM'
const SLACK_FRAUD_CHANNEL_ID = 'C0ANF8RHEK1'
const SLACK_SUSPICIOUS_CHANNEL_ID = 'C0AE5T4PJP8'

const state = {
  /** @type {Object|null} */
  response: null,
  /** @type {number|null} */
  fetchedAt: null,
}

/**
 * Compute statistics client-side from results list
 * @param {Array} results
 * @returns {Object|null}
 */
function _isFraud(flagCategory) {
  return flagCategory === 'Fraud' || flagCategory === 0
}

function _isSuspicious(flagCategory) {
  return flagCategory === 'Suspicious' || flagCategory === 1
}

function _computeStatistics(results) {
  if (!results || results.length === 0) {
    return null
  }

  const totalCount = results.length
  const fraudCount = results.filter(r => _isFraud(r.flagCategory)).length
  const suspiciousCount = results.filter(r => _isSuspicious(r.flagCategory)).length
  const importantCount = results.filter(r => r.notifyAdmins).length

  // Top sellers by total flagged count
  const sellerMap = {}
  for (const r of results) {
    const name = r.sellerName || 'Unknown'
    if (!sellerMap[name]) {
      sellerMap[name] = { sellerName: name, fraudCount: 0, suspiciousCount: 0, importantCount: 0 }
    }

    if (_isFraud(r.flagCategory)) {
      sellerMap[name].fraudCount++
    } else {
      sellerMap[name].suspiciousCount++
    }

    if (r.notifyAdmins) {
      sellerMap[name].importantCount++
    }
  }

  const topSellersByFraudCount = Object.values(sellerMap)
    .sort((a, b) => (b.fraudCount + b.suspiciousCount) - (a.fraudCount + a.suspiciousCount))

  // Filter tag breakdown
  const tagMap = {}
  for (const r of results) {
    if (!r.filterTags) {
      continue
    }

    const tags = r.filterTags.split(',').map(t => t.trim()).filter(t => t)
    for (const tag of tags) {
      if (!tagMap[tag]) {
        tagMap[tag] = { tag, fraudCount: 0, suspiciousCount: 0, importantCount: 0 }
      }

      if (_isFraud(r.flagCategory)) {
        tagMap[tag].fraudCount++
      } else {
        tagMap[tag].suspiciousCount++
      }

      if (r.notifyAdmins) {
        tagMap[tag].importantCount++
      }
    }
  }

  const byFilterTag = Object.values(tagMap)
    .sort((a, b) => (b.fraudCount + b.suspiciousCount) - (a.fraudCount + a.suspiciousCount))

  // Category name breakdown
  const categoryMap = {}
  for (const r of results) {
    const name = r.categoryName || 'Unknown'
    if (!categoryMap[name]) {
      categoryMap[name] = { categoryName: name, fraudCount: 0, suspiciousCount: 0, importantCount: 0 }
    }

    if (_isFraud(r.flagCategory)) {
      categoryMap[name].fraudCount++
    } else {
      categoryMap[name].suspiciousCount++
    }

    if (r.notifyAdmins) {
      categoryMap[name].importantCount++
    }
  }

  const topCategories = Object.values(categoryMap)
    .sort((a, b) => (b.fraudCount + b.suspiciousCount) - (a.fraudCount + a.suspiciousCount))

  return { totalCount, fraudCount, suspiciousCount, importantCount, topSellersByFraudCount, byFilterTag, topCategories }
}

/**
 * @type {import('vuex').GetterTree<typeof state>}
 */
const getters = {
  results(state) {
    return state.response?.results || []
  },

  statistics(state, getters) {
    return _computeStatistics(getters.results)
  },

  slackTeamId() {
    return SLACK_TEAM_ID
  },

  slackFraudChannelId() {
    return SLACK_FRAUD_CHANNEL_ID
  },

  slackSuspiciousChannelId() {
    return SLACK_SUSPICIOUS_CHANNEL_ID
  },
}

/**
 * @type {import('vuex').ActionTree<typeof state>}
 */
const actions = {
  async fetchResults({ commit, state }, { force = false } = {}) {
    if (!force && state.fetchedAt && (Date.now() - state.fetchedAt) < CACHE_TTL_MS) {
      return
    }

    const response = await FraudsApi.getResults()
    commit('SET_RESPONSE', response.data)
    return response
  },

  async fetchResultById(_, id) {
    const response = await FraudsApi.getResultById(id)
    return response.data
  },

  async updateStatus({ state, commit }, { id, status }) {
    await FraudsApi.updateStatus(id, status)

    if (state.response && state.response.results) {
      const updated = state.response.results.map(r =>
        r.callId === id ? { ...r, status } : r
      )
      commit('SET_RESPONSE', { ...state.response, results: updated })
    }
  },
}

/**
 * @type {import('vuex').MutationTree<typeof state>}
 */
const mutations = {
  SET_RESPONSE(state, data) {
    state.response = data || null
    state.fetchedAt = data ? Date.now() : null
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
